
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { UserRole, ExtendedProfile, Tenant } from '@/types/crm';
import { useToast } from '@/hooks/use-toast';

interface AuthContext {
  user: User | null;
  userRole: UserRole | null;
  profile: ExtendedProfile | null;
  tenant: Tenant | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContext | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Dezelfde context, maar zonder te gooien: undefined buiten een AuthProvider (toetsen, proefpagina's). */
export const useAuthOptioneel = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [profile, setProfile] = useState<ExtendedProfile | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        return null;
      }

      return data?.role as UserRole;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  };

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return null;
      }

      // Check if tenant_id exists and fetch tenant data
      const tenantId = (profileData as any)?.tenant_id;
      if (tenantId) {
        const { data: tenantData, error: tenantError } = await supabase
          .from('tenants' as any)
          .select('*')
          .eq('id', tenantId)
          .single();

        if (!tenantError && tenantData) {
          setTenant(tenantData as unknown as Tenant);
          setProfile({ ...profileData, tenant: tenantData as unknown as Tenant } as ExtendedProfile);
          return { profile: profileData, tenant: tenantData };
        }
      }

      setProfile(profileData as ExtendedProfile);
      return { profile: profileData, tenant: null };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  const hasRole = (role: string) => {
    return userRole === role;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // setTimeout(0) defers state updates to avoid calling Supabase
          // inside the synchronous onAuthStateChange callback, which can
          // cause a deadlock with the auth state machine.
          setTimeout(() => {
            Promise.all([
              fetchUserRole(session.user.id),
              fetchUserProfile(session.user.id)
            ]).then(([role]) => {
              setUserRole(role);
              setLoading(false);
            }).catch((error) => {
              console.error('Auth initialization failed:', error);
              setLoading(false);
            });
          }, 0);
        } else {
          setUserRole(null);
          setProfile(null);
          setTenant(null);
          setLoading(false);
        }

        if (event === 'PASSWORD_RECOVERY') {
          window.location.replace('/auth/reset');
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        Promise.all([
          fetchUserRole(session.user.id),
          fetchUserProfile(session.user.id)
        ]).then(([role]) => {
          setUserRole(role);
          setLoading(false);
        }).catch((error) => {
          console.error('Session initialization failed:', error);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        title: 'Login Error',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/crm`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });

    if (error) {
      toast({
        title: 'Registratie Error',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth/reset`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });

    if (error) {
      toast({
        title: 'Reset Error',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      toast({
        title: 'Wachtwoord wijzigen mislukt',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: 'Logout Error',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        userRole,
        profile,
        tenant,
        loading, 
        signIn, 
        signUp, 
        signOut, 
        resetPassword, 
        updatePassword, 
        hasRole 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
